import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { NgSelectModule } from "@ng-select/ng-select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { finalize } from "rxjs/operators";
import {
  forkJoin,
  switchMap,
  Observable,
  of,
  catchError,
  map,
  Subscription,
} from "rxjs";
import * as XLSX from "xlsx";

import { TripService } from "../../services/trip.service";
import { AnagraphicService } from "../../services/anagraphic.service";
import { NotificationService } from "../../services/notification.service";
import { ErrorHelperService } from "../../services/error-helper.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../../environments/environment";

import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { LoadingListService } from "../../services/loading-list.service";
import { NewLoadingListDialogComponent } from "../loading-list/new-loading-list-dialog/new-loading-list-dialog.component";
import { LoadingLists } from "../../interfaces/LoadingLists.interfaces";

import { Warehouse } from "../../interfaces/Warehouses.interfaces";
import { Carrier, Dealer } from "../../interfaces/anagraphic.interfaces";
import {
  Trip,
  TripPoint,
  TripPointParcel,
  TripSearchFilters,
  TripTransportDocument,
  TripTransportDocumentItem,
  TripAttachRequest,
  TripInfoResponse,
  TripDocsEvent,
  TripStatusTotal,
  TripStatusTotals,
} from "../../interfaces/trip.interfaces";
import { FunctionService } from "../../services/function.service";
import { BaseComponent } from "../../base/base.component";
import {
  ScanInputDirective,
  normalizeScanCode,
} from "../../directives/scan-input.directive";

type TripTabKey =
  "route" | "loadingLists" | "transportDocuments" | "points" | "parcels";

interface StatusMeta {
  label: string;
  color: string;
  icon: string;
}

interface Brand {
  brandCode: string;
  brandName?: string;
}

interface ClientsBrands {
  clientCode: string;
  clientName?: string;
  brands: Brand[];
}

@Component({
  selector: "app-trip-management",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    NgSelectModule,
    MatTooltipModule,
    MatDialogModule,
    ScanInputDirective
  ],
  templateUrl: "./trip-management.component.html",
  styleUrls: ["./trip-management.component.css"],
})
export class TripManagementComponent extends BaseComponent implements OnInit {
  isLoading = false;
  isFiltersCollapsed = false;

  warehouses: Warehouse[] = [];
  filterableWarehouses: Warehouse[] = [];
  warehouseCodes: string[] = [];
  carriers: Carrier[] = [];
  dealers: Dealer[] = [];

  filterWarehouse: string | null = null;
  filterWarehouseInvalid = false;
  appliedWarehouse: string | null = null;

  filterWarehouseFrom: string | null = null;
  filterWarehouseTo: string | null = null;

  isSingleWarehouseUser(): boolean {
    return this.filterableWarehouses.length === 1;
  }

  myWarehouseCode(): string | null {
    return this.isSingleWarehouseUser()
      ? this.filterableWarehouses[0].warehouseCode
      : null;
  }

  createdDateFromStr = "";
  createdDateToStr = "";

  requestDateFromStr = "";
  requestDateToStr = "";

  filterStatus = "";
  filterTripType = "TRANSFER";

  /** Ruolo del magazzino selezionato nei punti del viaggio (carico / scarico / tutti). */
  filterWarehousePointRole: "ALL" | "LOADING" | "UNLOADING" = "ALL";
  readonly warehousePointRoles = [
    { value: "ALL", label: "Tutti i punti", icon: "fa-arrows-up-down", color: "#6c757d" },
    { value: "LOADING", label: "Punto di carico", icon: "fa-arrow-up", color: "#d4a017" },
    { value: "UNLOADING", label: "Punto di scarico", icon: "fa-arrow-down", color: "#c2255c" },
  ];

  readonly maxTripTourGroupsShown = 5;

  searchTripCode = "";
  searchLoadingListCode = "";
  searchTransportDocumentCode = "";
  searchParcelCode = "";

  availableStatuses: (StatusMeta & { value: string })[] = [
    { value: "", label: "Tutti", icon: "fa-circle", color: "#6c757d" },
    {
      value: "PLANNED",
      label: "Pianificato",
      icon: "fa-calendar-day",
      color: "#f57906",
    },
    {
      value: "IN_PROGRESS",
      label: "In corso",
      icon: "fa-truck-fast",
      color: "#297af5",
    },
    { value: "CLOSED", label: "Chiuso", icon: "fa-lock", color: "#198754" },
    {
      value: "DELETED",
      label: "Eliminato",
      icon: "fa-trash",
      color: "#dc3545",
    },
  ];

  availableTripTypes: (StatusMeta & { value: string })[] = [
    {
      value: "TRANSFER",
      label: "Trasferimenti",
      icon: "fa-right-left",
      color: "#297af5",
    },
    {
      value: "DELIVERY",
      label: "Consegne",
      icon: "fa-truck",
      color: "#198754",
    },
    { value: "", label: "Tutti", icon: "fa-circle", color: "#6c757d" },
  ];

  trips: Trip[] = [];
  itemsPerPage = 10;
  currentPage = 1;
  totalTripsCount = 0;
  totalTripsPages = 1;
  private appliedFilters: TripSearchFilters | null = null;
  private tripsSearchSub?: Subscription;
  private statusTotalsSub?: Subscription;

  activeTab: Record<number, TripTabKey> = {};
  parcelsByTrip: Record<number, TripTransportDocumentItem[]> = {};
  parcelsLoading: Record<number, boolean> = {};
  sendingDocsTrip: Record<number, boolean> = {};
  revertingDocsTrip: Record<number, boolean> = {};
  printingDoc: Record<number, boolean> = {};
  printingDDTByDoc: Record<number, boolean> = {};
  printingBulkDocs: Record<number, boolean> = {};
  selectedDocs: Record<number, boolean> = {};

  clientsBrands: ClientsBrands[] = [];

  collapsedTrips: Record<number, boolean> = {};
  selectedTrips: Record<number, boolean> = {};

  showSendDocsConfirmFor: Trip | null = null;
  showBulkSendDocsConfirm = false;

  selectedTripPoint: TripPoint | null = null;
  selectedTripPointTrip: Trip | null = null;
  tripPointParcels: TripPointParcel[] = [];
  tripPointParcelsLoading = false;
  tripPointParcelsSearch = "";
  tripPointParcelsOnlyUrgent = false;
  tripPointParcelsTab: "load" | "unload" = "load";

  isTripReportCollapsed = false;

  summaryTripPlanned = 0;
  summaryTripInProgress = 0;
  summaryTripClosed = 0;
  summaryTripDeleted = 0;

  totTripPlannedItems = 0;
  totTripPlannedWeight = 0;
  totTripPlannedVolume = 0;
  totTripInProgressItems = 0;
  totTripInProgressWeight = 0;
  totTripInProgressVolume = 0;
  totTripClosedItems = 0;
  totTripClosedWeight = 0;
  totTripClosedVolume = 0;
  totTripDeletedItems = 0;
  totTripDeletedWeight = 0;
  totTripDeletedVolume = 0;

  docsHistoryPopupFor: Trip | null = null;
  docsHistoryEvents: TripDocsEvent[] = [];
  docsHistoryLoading = false;

  private readonly docsEventMeta: Record<
    string,
    { label: string; icon: string; cssClass: string }
  > = {
    SEND_REQUESTED: {
      label: "Richiesta invio bolle",
      icon: "fa-hourglass-half",
      cssClass: "ev-requested",
    },
    DOCS_SENT: {
      label: "Bolle inviate",
      icon: "fa-envelope-open",
      cssClass: "ev-sent",
    },
    DOCS_SKIPPED_NO_RECIPIENTS: {
      label: "Destinatari mancanti per alcune bolle",
      icon: "fa-triangle-exclamation",
      cssClass: "ev-warning",
    },
    SEND_REVERTED: {
      label: "Richiesta di invio annullata",
      icon: "fa-rotate-left",
      cssClass: "ev-neutral",
    },
    DOCS_SENT_RESET: {
      label: "Invio resettato per modifica viaggio",
      icon: "fa-arrows-rotate",
      cssClass: "ev-neutral",
    },
  };

  constructor(
    private tripService: TripService,
    private anagraphicService: AnagraphicService,
    private notificationService: NotificationService,
    private errorHelperService: ErrorHelperService,
    private http: HttpClient,
    private loadingListService: LoadingListService,
    private dialog: MatDialog,
    functionService: FunctionService,
    router: Router,
  ) {
    super(functionService, router);
  }

  ngOnInit(): void {
    const stored = localStorage.getItem("userFunctions");

    if (stored) {
      const parsed = JSON.parse(stored);
      this.clientsBrands = parsed.clients;
    }

    this.loadWarehouses();
    this.loadCarriers();
    this.loadDealers();

    const todayStr = this.toYyyyMmDd(new Date());
    this.createdDateFromStr = todayStr;
    this.createdDateToStr = todayStr;
  }

  loadWarehouses(): void {
    this.http
      .get<Warehouse[]>(`${environment.apiUrl}/tracking/warehouses`)
      .subscribe({
        next: (data) => {
          this.warehouses = data;
          this.filterableWarehouses = data;

          const stored = localStorage.getItem("userFunctions");
          if (stored) {
            const parsed = JSON.parse(stored);
            this.warehouseCodes = parsed.warehouses;
            this.filterableWarehouses = this.filterableWarehouses.filter((w) =>
              this.warehouseCodes.includes(w.warehouseCode),
            );
          }

          if (this.filterableWarehouses.length === 1) {
            this.filterWarehouse = this.filterableWarehouses[0].warehouseCode;
            this.search();
          }
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(
            `Errore nel caricamento magazzini: ${message}`,
          );
        },
      });
  }

  loadCarriers(): void {
    this.tripService.loadCarriers().subscribe({
      next: (data) => (this.carriers = data),
      error: () => {},
    });
  }

  loadDealers(): void {
    this.tripService.loadDealers(this.clientsBrands).subscribe({
      next: (dealers) => {
        this.dealers = dealers;
      },
      error: (err) => {
        const message = this.errorHelperService.extractBackendMessage(err);
        this.notificationService.error(`Errore: ${message}`);
      },
    });
  }

  searchWarehouseFn(term: string, item: any): boolean {
    const t = term.toLowerCase();
    return (
      item.warehouseName?.toLowerCase().includes(t) ||
      item.warehouseShortName?.toLowerCase().includes(t) ||
      item.warehouseCode?.toLowerCase().includes(t)
    );
  }

  onWarehouseChange(): void {
    if (this.filterWarehouse) {
      this.filterWarehouseInvalid = false;
    }
  }

  toggleFiltersCollapsed(): void {
    this.isFiltersCollapsed = !this.isFiltersCollapsed;
  }

  shiftCreatedDates(days: number): void {
    const from = this.fromYyyyMmDd(this.createdDateFromStr) ?? new Date();
    const to = this.fromYyyyMmDd(this.createdDateToStr) ?? new Date();

    this.createdDateFromStr = this.toYyyyMmDd(this.addDays(from, days));
    this.createdDateToStr = this.toYyyyMmDd(this.addDays(to, days));
  }

  shiftRequestDates(days: number): void {
    const from = this.fromYyyyMmDd(this.requestDateFromStr) ?? new Date();
    const to = this.fromYyyyMmDd(this.requestDateToStr) ?? new Date();

    this.requestDateFromStr = this.toYyyyMmDd(this.addDays(from, days));
    this.requestDateToStr = this.toYyyyMmDd(this.addDays(to, days));
  }

  private addDays(date: Date, days: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  private fromYyyyMmDd(value: string): Date | null {
    if (!value) return null;
    const [y, m, d] = value.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }

  buildFilters(): TripSearchFilters {
    return {
      warehouseCode: this.filterWarehouse as string,
      createdDateFrom: this.toStartOfDayIso(this.createdDateFromStr),
      createdDateTo: this.toEndOfDayIso(this.createdDateToStr),
      requestDateFrom: this.toStartOfDayIso(this.requestDateFromStr),
      requestDateTo: this.toEndOfDayIso(this.requestDateToStr),
      status: this.filterStatus || null,
      tripCode: this.searchTripCode?.trim() || null,
      loadingListCode: this.searchLoadingListCode?.trim() || null,
      transportDocumentCode: this.searchTransportDocumentCode?.trim() || null,
      parcelCode: this.searchParcelCode?.trim() || null,
      warehouseFromCode: this.filterWarehouseFrom || null,
      warehouseToCode: this.filterWarehouseTo || null,
      restrictWarehouseCode: this.isSingleWarehouseUser()
        ? this.myWarehouseCode()
        : null,
      tripType: this.filterTripType || null,
      // Solo con un magazzino selezionato: viaggi con il magazzino in almeno un punto di carico/scarico
      warehousePointRole:
        this.filterWarehouse && this.filterWarehousePointRole !== "ALL"
          ? this.filterWarehousePointRole
          : null,
    } as TripSearchFilters;
  }

  search(): void {
    if (!this.filterWarehouse && !this.canSearchWithoutWarehouse()) {
      this.filterWarehouseInvalid = true;
      this.notificationService.error(
        "Seleziona il magazzino per effettuare la ricerca",
      );
      return;
    }

    this.filterWarehouseInvalid = false;
    this.appliedWarehouse = this.filterWarehouse;
    this.appliedFilters = this.buildFilters();

    this.loadTripsPage(1);
    this.loadStatusTotals();
  }

  /** Ricarica la pagina corrente e il riepilogo con i filtri dell'ultima ricerca (dopo una modifica a un viaggio). */
  refreshTrips(): void {
    if (!this.appliedFilters) {
      this.search();
      return;
    }
    this.loadTripsPage(this.currentPage);
    this.loadStatusTotals();
  }

  reloadTrip(tripId: number): void {
    if (!tripId) return;

    let params = new HttpParams();
    const filters = this.appliedFilters as any;
    const restrict = filters?.restrictWarehouseCode;
    const warehouse = filters?.warehouseCode;
    if (restrict) params = params.set("restrictWarehouseCode", restrict);
    if (warehouse) params = params.set("warehouseCode", warehouse);

    this.http
      .get<Trip>(`${environment.apiUrl}/trips/${tripId}`, { params })
      .subscribe({
        next: (fresh) => {
          if (!fresh) return;
          const index = this.trips.findIndex((t) => t.tripId === tripId);
          if (index < 0) return;
          this.trips = this.trips.map((t, i) => (i === index ? fresh : t));
        },
        error: () => this.refreshTrips(),
      });

    this.loadStatusTotals();
  }

  private loadTripsPage(page: number): void {
    if (!this.appliedFilters) return;

    // Annulla la richiesta precedente: con cambi pagina rapidi le risposte possono arrivare fuori ordine
    this.tripsSearchSub?.unsubscribe();
    this.isLoading = true;

    this.tripsSearchSub = this.tripService
      .search({ ...this.appliedFilters, page, pageSize: this.itemsPerPage })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data) => {
          this.totalTripsCount = data?.totalCount || 0;
          this.totalTripsPages = Math.max(1, data?.totalPages || 0);

          // La pagina richiesta non esiste più (es. eliminato l'ultimo viaggio dell'ultima pagina): ricarica l'ultima
          if (page > this.totalTripsPages) {
            this.loadTripsPage(this.totalTripsPages);
            return;
          }

          this.currentPage = data?.page || page;
          this.trips = data?.trips || [];
          this.trips.forEach((t) => {
            if (!this.activeTab[t.tripId]) {
              this.activeTab[t.tripId] = "route";
            }
            if (this.collapsedTrips[t.tripId] == null) {
              this.collapsedTrips[t.tripId] = false;
            }
            if (this.selectedTrips[t.tripId] == null) {
              this.selectedTrips[t.tripId] = false;
            }
          });
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(
            `Errore nella ricerca dei viaggi: ${message}`,
          );
        },
      });
  }

  resetFilters(): void {
    this.createdDateFromStr = "";
    this.createdDateToStr = "";
    this.requestDateFromStr = "";
    this.requestDateToStr = "";
    this.filterStatus = "";
    this.filterTripType = "TRANSFER";
    this.filterWarehousePointRole = "ALL";
    this.searchTripCode = "";
    this.searchLoadingListCode = "";
    this.searchTransportDocumentCode = "";
    this.searchParcelCode = "";
    this.filterWarehouseFrom = null;
    this.filterWarehouseTo = null;
    this.filterWarehouse = null;
    this.search();
  }

  get tripsPageNumbers(): number[] {
    const total = this.totalTripsPages;
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(total, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);

    const pages: number[] = [];
    for (let p = start; p <= end; p++) {
      pages.push(p);
    }
    return pages;
  }

  setTripsItemsPerPage(n: number): void {
    if (this.itemsPerPage === n) return;
    this.itemsPerPage = n;
    this.loadTripsPage(1);
  }

  goToTripsPage(page: number): void {
    const target = Math.min(Math.max(1, page), this.totalTripsPages);
    if (target === this.currentPage) return;
    this.loadTripsPage(target);
  }

  goToFirstTripsPage(): void {
    this.goToTripsPage(1);
  }

  goToPrevTripsPage(): void {
    this.goToTripsPage(this.currentPage - 1);
  }

  goToNextTripsPage(): void {
    this.goToTripsPage(this.currentPage + 1);
  }

  goToLastTripsPage(): void {
    this.goToTripsPage(this.totalTripsPages);
  }

  selectTab(trip: Trip, tab: TripTabKey): void {
    this.activeTab[trip.tripId] = tab;

    if (
      tab === "parcels" &&
      !this.parcelsByTrip[trip.tripId] &&
      !this.parcelsLoading[trip.tripId]
    ) {
      this.loadParcels(trip);
    }
  }

  isTabActive(trip: Trip, tab: TripTabKey): boolean {
    return (this.activeTab[trip.tripId] || "route") === tab;
  }

  loadParcels(trip: Trip): void {
    this.parcelsLoading[trip.tripId] = true;

    this.tripService
      .getParcelsByTrip(trip.tripId)
      .pipe(finalize(() => (this.parcelsLoading[trip.tripId] = false)))
      .subscribe({
        next: (items) => (this.parcelsByTrip[trip.tripId] = items || []),
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(
            `Errore nel caricamento colli: ${message}`,
          );
        },
      });
  }

  getVisibleLoadingLists(trip: Trip): any[] {
    return trip.loadingLists || [];
  }

  canSearchWithoutWarehouse(): boolean {
    return this.filterTripType === "DELIVERY" && this.isDeliveryTripsViewer();
  }

  getParcelsForTrip(trip: Trip): TripTransportDocumentItem[] {
    const all = this.parcelsByTrip[trip.tripId] || [];
    if (!this.isSingleWarehouseUser() || this.isExternalTrip(trip)) {
      return all;
    }
    const myWh = this.myWarehouseCode();
    return all.filter(
      (it: any) =>
        it.parcelComplementaryWarehouseFrom === myWh ||
        it.parcelComplementaryWarehouseTo === myWh,
    );
  }

  getVisibleTransportDocuments(trip: Trip): TripTransportDocument[] {
    return trip.transportDocuments || [];
  }

  selectedTransportDocument: TripTransportDocument | null = null;
  selectedTransportDocumentTrip: Trip | null = null;

  openTransportDocumentParcelsPopup(
    trip: Trip,
    doc: TripTransportDocument,
  ): void {
    this.selectedTransportDocumentTrip = trip;
    this.selectedTransportDocument = doc;

    if (!this.parcelsByTrip[trip.tripId] && !this.parcelsLoading[trip.tripId]) {
      this.loadParcels(trip);
    }
  }

  closeTransportDocumentParcelsPopup(): void {
    this.selectedTransportDocument = null;
    this.selectedTransportDocumentTrip = null;
  }

  getTransportDocumentParcels(): TripTransportDocumentItem[] {
    if (!this.selectedTransportDocument || !this.selectedTransportDocumentTrip)
      return [];
    return this.getParcelsForTrip(this.selectedTransportDocumentTrip).filter(
      (item) =>
        item.transportDocumentCode ===
        this.selectedTransportDocument!.transportDocumentCode,
    );
  }

  exportTripParcelsToExcel(trip: Trip): void {
    const items = this.getParcelsForTrip(trip);
    if (!items.length) return;

    const rows = items.map((it) => ({
      "Codice Collo": it.transportDocumentItemParcelCode || "-",
      Barcode: it.transportDocumentItemBarcode || "-",
      "Peso Kg": it.transportDocumentItemParcelWeight ?? "",
      "Volume M3": it.transportDocumentItemParcelVolume ?? "",
      Viaggio: trip.tripCode,
      "Documento Trasporto": it.transportDocumentCode || "-",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0]).map((key) => ({
      wch: Math.max(15, key.length + 2),
    }));
    XLSX.utils.book_append_sheet(wb, ws, "Colli");

    const fileName = `colli_${trip.tripCode}_${this.nowStamp()}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }

  getStatusMeta(status: string | null | undefined): StatusMeta {
    const found = this.availableStatuses.find((s) => s.value === status);
    if (found) return found;
    return { label: status || "N/D", color: "#6c757d", icon: "fa-circle" };
  }

  getSortedPoints(trip: Trip): TripPoint[] {
    return [...(trip.tripPoints || [])].sort(
      (a, b) => (a.tripPointSequence ?? 0) - (b.tripPointSequence ?? 0),
    );
  }

  getPointIcon(point: TripPoint | any): string {
    switch (point.tripPointOperation) {
      case "LOADING":
        return "fa-arrow-up";
      case "UNLOADING":
        return "fa-arrow-down";
      case "LOADING_UNLOADING":
        return "fa-arrows-up-down";
      default:
        return "fa-location-dot";
    }
  }

  getPointOperationLabel(point: TripPoint | any): string {
    switch (point.tripPointOperation) {
      case "LOADING":
        return "Carico";
      case "UNLOADING":
        return "Scarico";
      case "LOADING_UNLOADING":
        return "Carico/Scarico";
      default:
        return point.tripPointOperation || "";
    }
  }

  getMatTooltipTripPointRoutNode(point: TripPoint | any): string {
    if (point.tripPointOperation === "UNLOADING") {
      return "";
    }
    return "Clicca per vederne il dettaglio";
  }

  getPointStatus(point: TripPoint): "PLANNED" | "IN_PROGRESS" | "CLOSED" {
    const needsLoad =
      point.tripPointOperation === "LOADING" ||
      point.tripPointOperation === "LOADING_UNLOADING";
    const needsUnload =
      point.tripPointOperation === "UNLOADING" ||
      point.tripPointOperation === "LOADING_UNLOADING";

    const loadDone =
      !needsLoad ||
      !!point.tripPointLoadedAt ||
      point.tripPointLoadingStatus === "CLOSED";
    const unloadDone =
      !needsUnload ||
      !!point.tripPointUnloadedAt ||
      point.tripPointUnloadingStatus === "CLOSED";

    if (loadDone && unloadDone) return "CLOSED";

    const loadStarted =
      point.tripPointLoadingStatus === "IN_PROGRESS" ||
      point.tripPointLoadingStatus === "CLOSED" ||
      !!point.tripPointLoadedAt;
    const unloadStarted =
      point.tripPointUnloadingStatus === "IN_PROGRESS" ||
      point.tripPointUnloadingStatus === "CLOSED" ||
      !!point.tripPointUnloadedAt;

    if (loadStarted || unloadStarted) return "IN_PROGRESS";

    return "PLANNED";
  }

  getPointStatusColor(point: TripPoint): string {
    const status = this.getPointStatus(point);
    if (status === "CLOSED") return "#00a651"; // verde
    if (status === "IN_PROGRESS") return "#0d6efd"; // arancio
    return "#adb5bd"; // grigio
  }

  getConnectorState(
    trip: Trip,
    index: number,
  ): "done" | "current" | "planning" {
    const points = this.getSortedPoints(trip);
    const current = points[index];
    const next = points[index + 1];
    if (!current) return "planning";

    const currentDone = this.getPointStatus(current) === "CLOSED";
    const nextDone = next ? this.getPointStatus(next) === "CLOSED" : false;

    if (currentDone && nextDone) return "done";
    if (currentDone && !nextDone) return "current";
    return "planning";
  }

  getPointStatusLabel(point: TripPoint): string {
    const status = this.getPointStatus(point);
    if (status === "CLOSED") return "Completata";
    if (status === "IN_PROGRESS") return "In corso";
    return "In attesa";
  }

  getPointSubStatusLabel(status: string | null | undefined): string {
    if (status === "CLOSED") return "Completato";
    if (status === "IN_PROGRESS") return "In corso";
    return "In attesa";
  }

  getPointSubStatusColor(status: string | null | undefined): string {
    if (status === "CLOSED") return "#00a651";
    if (status === "IN_PROGRESS") return "#0d6efd";
    return "#adb5bd";
  }

  getOperationClass(point: TripPoint | any): string {
    switch (point.tripPointOperation) {
      case "LOADING":
        return "op-carico";
      case "UNLOADING":
        return "op-scarico";
      case "LOADING_UNLOADING":
        return "op-carico-scarico";
      default:
        return "op-generic";
    }
  }

  isMergeRoutePointDone(point: any): boolean {
    if (point?.routePointPosition === "PREVIOUS") return true;

    const needsLoad =
      point?.tripPointOperation === "LOADING" ||
      point?.tripPointOperation === "LOADING_UNLOADING";
    const needsUnload =
      point?.tripPointOperation === "UNLOADING" ||
      point?.tripPointOperation === "LOADING_UNLOADING";

    const loadDone = !needsLoad || point?.tripPointLoadingStatus === "CLOSED";
    const unloadDone =
      !needsUnload || point?.tripPointUnloadingStatus === "CLOSED";

    return loadDone && unloadDone;
  }

  /** Lunghezza massima del nome di ogni punto nella tratta in testata. */
  private readonly ROUTE_ENDPOINT_MAX_CHARS = 14;

  /**
   * Tutti i punti del viaggio in sequenza (short name magazzino o nome dealer). I nomi troppo
   * lunghi vengono troncati con i puntini e mostrati per intero nel tooltip.
   */
  getRouteEndpoints(trip: Trip): { label: string; tooltip: string; truncated: boolean }[] {
    return this.getSortedPoints(trip).map((point) => {
      const full = this.getPointShortLabel(point.tripPointWarehouseCode, point.tripPointDealerCode);
      const truncated = full.length > this.ROUTE_ENDPOINT_MAX_CHARS;
      return {
        label: truncated ? full.slice(0, this.ROUTE_ENDPOINT_MAX_CHARS - 1).trimEnd() + "…" : full,
        tooltip: this.getPointTooltip(point.tripPointWarehouseCode, point.tripPointDealerCode),
        truncated,
      };
    });
  }

  trackByIndex(index: number): number {
    return index;
  }

  getRouteFromLabel(trip: Trip): string {
    const points = this.getSortedPoints(trip);
    const first = points[0];
    if (!first) return "-";
    return this.getPointShortLabel(
      first.tripPointWarehouseCode,
      first.tripPointDealerCode,
    );
  }

  getRouteToLabel(trip: Trip): string {
    const points = this.getSortedPoints(trip);
    const last = points[points.length - 1];
    if (!last) return "-";
    return this.getPointShortLabel(
      last.tripPointWarehouseCode,
      last.tripPointDealerCode,
    );
  }

  hexToRgba(hex: string, alpha: number): string {
    if (!hex) return `rgba(108, 117, 125, ${alpha})`;
    const clean = hex.replace("#", "");
    const full =
      clean.length === 3
        ? clean
            .split("")
            .map((c) => c + c)
            .join("")
        : clean;
    const num = parseInt(full, 16);
    if (isNaN(num)) return `rgba(108, 117, 125, ${alpha})`;
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  getLoadingListUnloadedCount(ll: any): number {
    if (ll?.loadingListUnloadedCount != null)
      return ll.loadingListUnloadedCount;
    if (ll?.loadingListDeliveredCount != null)
      return ll.loadingListDeliveredCount;
    const status = ll?.loadingListStatus;
    if (status === "CLOSED" || status === "ARRIVED") {
      return ll?.loadingListItemsCount || 0;
    }
    return 0;
  }

  getLoadingListUnloadedCountDen(ll: any): number {
    if (ll?.loadingListIsCreatedOnArrival) return ll?.loadingListItemsCount;
    return ll?.loadingListLoadedCount || 0;
  }

  getLoadingListLoadedPercent(ll: any): number {
    const total = this.getLoadingListLoadedDenominator(ll);
    if (!total) return 0;
    return Math.round(((ll?.loadingListLoadedCount || 0) / total) * 100);
  }

  getLoadingListLoadedDenominator(ll: any): number {
    return ll?.loadingListItemsCount || 0;
  }

  getLoadingListUnloadedPercent(ll: any): number {
    let total = 0;
    if (ll?.loadingListIsCreatedOnArrival) {
      total = ll?.loadingListItemsCount;
    } else {
      total = ll?.loadingListLoadedCount || 0;
    }
    if (!total) return 0;
    return Math.round((this.getLoadingListUnloadedCount(ll) / total) * 100);
  }

  getParcelStatusInfo(item: any): { label: string; color: string } {
    if (item?.transportDocumentItemDeliveredAt || item?.parcelDeliveryDate) {
      return { label: "Consegnato", color: "#00a651" };
    }
    if (item?.transportDocumentItemUnloadedAt) {
      return { label: "Scaricato", color: "#0dcaf0" };
    }
    if (item?.transportDocumentItemLoadedAt) {
      return { label: "Caricato", color: "#297af5" };
    }
    return { label: "In attesa", color: "#adb5bd" };
  }

  getLoadingListStatusStyle(status?: string | null): any {
    switch ((status || "").toLowerCase()) {
      case "draft":
        return { backgroundColor: "rgba(245, 121, 6, 0.10)", color: "#f57906" };

      case "validated":
      case "prepared":
        return {
          backgroundColor: "rgba(41, 122, 245, 0.10)",
          color: "#297af5",
        };

      case "arrived":
      case "arriving":
        return {
          backgroundColor: "rgba(13, 202, 240, 0.10)",
          color: "#0dcaf0",
        };

      case "closed":
        return { backgroundColor: "rgba(25, 135, 84, 0.10)", color: "#198754" };

      case "deleted":
        return { backgroundColor: "rgba(220, 53, 69, 0.15)", color: "#dc3545" };

      default:
        return {
          backgroundColor: "rgba(108, 117, 125, 0.15)",
          color: "#6c757d",
        };
    }
  }

  getLoadingListStatusLabel(status?: string | null): string {
    switch ((status || "").toUpperCase()) {
      case "DRAFT":
        return "BOZZA";

      case "PREPARED":
        return "PREPARATO";

      case "ARRIVED":
        return "ARRIVATO";
      case "ARRIVING":
        return "IN ARRIVO";

      case "CLOSED":
        return "CHIUSO";

      case "DELETED":
        return "ELIMINATO";

      default:
        return status || "UNKNOWN";
    }
  }

  getLoadingListType(
    ll: any,
    trip?: Trip,
  ): "DEPARTURE" | "ARRIVAL" | "LINE_CHECK" {
    if (
      (ll?.loadingListKind || "").toString().trim().toUpperCase() ===
      "LINE_CHECK"
    ) {
      return "LINE_CHECK";
    }

    const wh = (ll?.loadingListWarehouseCode || "")
      .toString()
      .trim()
      .toLowerCase();
    const referenceWarehouse =
      trip && this.isExternalTrip(trip)
        ? trip.tripWarehouseFromCode
        : this.appliedWarehouse;
    const filtered = (referenceWarehouse || "").toString().trim().toLowerCase();

    return wh && filtered && wh === filtered ? "DEPARTURE" : "ARRIVAL";
  }

  getLoadingListTypeMeta(
    ll: any,
    trip?: Trip,
  ): { label: string; icon: string; color: string; bg: string } {
    const type = this.getLoadingListType(ll, trip);

    if (type === "LINE_CHECK") {
      return {
        label: "CONTROLLO FILA",
        icon: "fa-list-check",
        color: "#fd7e14",
        bg: "rgba(253, 126, 20, 0.12)",
      };
    }

    if (type === "DEPARTURE") {
      return {
        label: "PARTENZA",
        icon: "fa-arrow-up",
        color: "#6f42c1",
        bg: "rgba(111, 66, 193, 0.12)",
      };
    }

    return {
      label: "ARRIVO",
      icon: "fa-arrow-down",
      color: "#20c997",
      bg: "rgba(32, 201, 151, 0.12)",
    };
  }

  mergeTripPopupFor: any | null = null;
  mergeTripPopupStep: "scan" | "confirm" = "scan";
  mergeTripScannedCode: string = "";
  mergeTripResolving: boolean = false;
  mergeTripResolveError: string | null = null;
  mergeTripResolvedTrip: TripInfoResponse | null = null;
  mergeTripConfirming: boolean = false;
  mergeTripConfirmError: string | null = null;
  /** Id del viaggio SORGENTE (quello in cui si trova attualmente il borderò da spostare) */
  mergeTripSourceTripId: number | null = null;

  canMergeLoadingListToTrip(ll: any): boolean {
    return (
      (ll?.loadingListStatus || "").toString().toUpperCase() === "PREPARED"
    );
  }

  canDeleteLoadingListToTrip(ll: any): boolean {
    return (
      (ll?.loadingListStatus || "").toString().toUpperCase() === "PREPARED" ||
      (ll?.loadingListStatus || "").toString().toUpperCase() === "DRAFT"
    );
  }

  showDeletePopup = false;
  deleteTarget: any = null;
  deleting = false;
  requireDeleteTextConfirm = false;
  deleteConfirmText = "";

  openDeletePopup(ll: any): void {
    if (!this.canDeleteLoadingListToTrip(ll)) return;
    this.deleteTarget = ll;
    this.deleteConfirmText = "";
    this.showDeletePopup = true;
  }

  closeDeletePopup(): void {
    if (this.deleting) return;
    this.showDeletePopup = false;
    this.deleteTarget = null;
  }

  confirmDeletePopup(): void {
    if (!this.deleteTarget || this.deleting) return;

    this.deleting = true;

    this.loadingListService
      .deleteLoadingList(this.deleteTarget.loadingListId)
      .subscribe({
        next: () => {
          this.deleting = false;
          this.closeDeletePopup();
          this.refreshTrips();
          this.notificationService.success("Borderò eliminato con successo");
        },
        error: (err) => {
          this.deleting = false;
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(`Errore: ${message}`);
        },
      });
  }

  openMergeLoadingListToTripDialog(ll: any, trip: Trip): void {
    this.mergeTripPopupFor = ll;
    this.mergeTripSourceTripId = trip.tripId;
    this.mergeTripPopupStep = "scan";
    this.mergeTripScannedCode = "";
    this.mergeTripResolveError = null;
    this.mergeTripResolvedTrip = null;
    this.mergeTripConfirmError = null;
  }

  closeMergeTripPopup(): void {
    this.mergeTripPopupFor = null;
    this.mergeTripSourceTripId = null;
  }

  resolveMergeTripScannedCode(): void {
    const code = (this.mergeTripScannedCode || "").trim();
    if (!code || this.mergeTripResolving || !this.mergeTripPopupFor) {
      return;
    }

    this.mergeTripResolving = true;
    this.mergeTripResolveError = null;

    const request: Partial<TripAttachRequest> = {
      scannedCode: code,
      loadingListId: this.mergeTripPopupFor.loadingListId,
      warehouseCode: this.appliedWarehouse,
    };

    this.http
      .post<TripInfoResponse>(
        `${environment.apiUrl}/trips/resolve-link`,
        request,
      )
      .pipe(finalize(() => (this.mergeTripResolving = false)))
      .subscribe({
        next: (trip) => {
          this.mergeTripResolvedTrip = trip;
          this.mergeTripPopupStep = "confirm";
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.mergeTripResolveError =
            message || "Codice non riconosciuto o viaggio non trovato";
        },
      });
  }

  backToMergeTripScanStep(): void {
    this.mergeTripPopupStep = "scan";
    this.mergeTripResolvedTrip = null;
    this.mergeTripConfirmError = null;
  }

  confirmMergeTrip(): void {
    if (
      !this.mergeTripResolvedTrip ||
      this.mergeTripConfirming ||
      !this.mergeTripPopupFor ||
      this.mergeTripSourceTripId == null
    ) {
      return;
    }

    this.mergeTripConfirming = true;
    this.mergeTripConfirmError = null;

    const ll = this.mergeTripPopupFor;

    const mergeRequest: Partial<TripAttachRequest> = {
      tripId: this.mergeTripSourceTripId,
      scannedCode: this.mergeTripResolvedTrip.tripCode,
      warehouseCode: this.appliedWarehouse,
      forceCarrier: true,
    };

    this.http
      .post(`${environment.apiUrl}/trips/attach-loading-list`, mergeRequest)
      .pipe(finalize(() => (this.mergeTripConfirming = false)))
      .subscribe({
        next: () => {
          this.notificationService.success(
            `Borderò ${ll.loadingListCode} spostato con successo`,
          );
          this.closeMergeTripPopup();
          this.refreshTrips();
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.mergeTripConfirmError =
            message || "Errore durante lo spostamento sul nuovo viaggio";
        },
      });
  }

  detachPopupFor: { ll: any; trip: Trip } | null = null;
  detaching = false;
  detachError: string | null = null;

  canDetachLoadingListFromTrip(ll: any, trip: Trip): boolean {
    return !!ll?.loadingListCanDetachFromTrip && !this.isExternalTrip(trip);
  }

  openDetachLoadingListPopup(ll: any, trip: Trip): void {
    if (!this.canDetachLoadingListFromTrip(ll, trip)) return;
    this.detachPopupFor = { ll, trip };
    this.detachError = null;
  }

  closeDetachLoadingListPopup(): void {
    if (this.detaching) return;
    this.detachPopupFor = null;
    this.detachError = null;
  }

  confirmDetachLoadingList(): void {
    if (!this.detachPopupFor || this.detaching) return;

    const { ll, trip } = this.detachPopupFor;

    const request = {
      loadingListId: ll.loadingListId,
      tripId: trip.tripId,
      warehouseCode: ll.loadingListWarehouseCode || this.appliedWarehouse,
    } as TripAttachRequest;

    this.detaching = true;
    this.detachError = null;

    this.tripService
      .detachLoadingListFromTrip(request)
      .pipe(finalize(() => (this.detaching = false)))
      .subscribe({
        next: (res: TripInfoResponse) => {
          const newTripCode = (res as any)?.tripCode;
          // Bozza o preparato: in entrambi i casi viene creato un nuovo viaggio con il solo borderò
          this.notificationService.success(
            `Borderò ${ll.loadingListCode} sfilato dal viaggio ${trip.tripCode}` +
              (newTripCode ? `: creato il nuovo viaggio ${newTripCode}` : ""),
          );
          this.detachPopupFor = null;
          this.refreshTrips();
        },
        error: (err) => {
          const message = (
            this.errorHelperService.extractBackendMessage(err) || ""
          )
            .replace(/^(\s*<br\s*\/?>\s*)+/i, "")
            .trim();
          this.detachError =
            message || "Errore durante lo scollegamento dal viaggio";
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Popup data/ora di partenza (punto di carico) / arrivo (punto di scarico) navetta.
  // La partenza registra solo la data: vettore trazionista, autista e sigillo si inseriscono
  // prima con il popup "Arrivo autista" (vedi sotto); qui il sigillo e' mostrato in sola lettura.
  // ---------------------------------------------------------------------------
  shuttleDatePopupFor: { trip: Trip; point: TripPoint } | null = null;
  shuttleDepartureDateStr = "";
  shuttleDepartureTimeStr = "";
  shuttleArrivalDateStr = "";
  shuttleArrivalTimeStr = "";
  shuttleSaving = false;

  shuttleStep: "input" | "confirm" = "input";
  shuttleValidationError = "";

  shuttleDepartureAlreadySet = false;
  shuttleArrivalAlreadySet = false;

  /** Sigillo registrato con l'arrivo autista: nel popup di partenza e' solo consultabile. */
  shuttleLockCode = "";

  /** Colonna da cui e' stato aperto il popup: partenza e arrivo ora sono gestiti separatamente. */
  shuttlePopupMode: "departure" | "arrival" = "departure";

  /** L'utente mono-magazzino puo' gestire la navetta solo sui punti del proprio magazzino. */
  canManageShuttle(point: TripPoint | any): boolean {
    return (
      !this.isSingleWarehouseUser() ||
      point?.tripPointWarehouseCode === this.myWarehouseCode()
    );
  }

  get shuttlePopupNeedsDeparture(): boolean {
    return (
      this.shuttlePopupMode === "departure" &&
      this.isDeparturePoint(this.shuttleDatePopupFor?.point)
    );
  }

  get shuttlePopupNeedsArrival(): boolean {
    return (
      this.shuttlePopupMode === "arrival" &&
      this.isArrivalPoint(this.shuttleDatePopupFor?.point)
    );
  }

  get shuttlePopupTitle(): string {
    return this.shuttlePopupMode === "departure"
      ? "Data di partenza"
      : "Data di arrivo";
  }

  get shuttleDepartureEditable(): boolean {
    return this.shuttlePopupNeedsDeparture && !this.shuttleDepartureAlreadySet;
  }

  get shuttleArrivalEditable(): boolean {
    return this.shuttlePopupNeedsArrival && !this.shuttleArrivalAlreadySet;
  }

  /** Popup aperto su un punto con tutte le date gia' registrate: solo consultazione. */
  get shuttleViewOnly(): boolean {
    return (
      !!this.shuttleDatePopupFor &&
      !this.shuttleDepartureEditable &&
      !this.shuttleArrivalEditable
    );
  }

  get todayDateStr(): string {
    return this.toYyyyMmDd(new Date());
  }

  /** La partenza non puo' precedere l'arrivo autista: limite minimo del campo data. */
  get shuttleDepartureMinDateStr(): string | null {
    const readyAt = this.getDriverReadyAt(this.shuttleDatePopupFor?.point);
    return readyAt ? this.dateTimeParts(readyAt).dateStr : null;
  }

  get shuttleConfirmMessage(): string {
    const parts: string[] = [];
    if (this.shuttleDepartureEditable) {
      parts.push(
        `la data di partenza il ${this.formatItDate(this.shuttleDepartureDateStr)} alle ${this.shuttleDepartureTimeStr}`,
      );
    }
    if (this.shuttleArrivalEditable) {
      parts.push(
        `la data di arrivo il ${this.formatItDate(this.shuttleArrivalDateStr)} alle ${this.shuttleArrivalTimeStr}`,
      );
    }
    return `Sei sicuro di voler dichiarare ${parts.join(" e ")}? L'operazione non potrà essere modificata in seguito.`;
  }

  private formatItDate(dateStr: string): string {
    const [y, m, d] = (dateStr || "").split("-");
    return d && m && y ? `${d}/${m}/${y}` : dateStr;
  }

  private isFutureDateTime(dateStr: string, timeStr: string): boolean {
    if (!dateStr || !timeStr) {
      return false;
    }
    return (
      new Date(this.combineDateTimeIso(dateStr, timeStr)).getTime() > Date.now()
    );
  }

  private nowDateTimeParts(): { dateStr: string; timeStr: string } {
    const now = new Date();
    return {
      dateStr: this.toYyyyMmDd(now),
      timeStr: `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
    };
  }

  private dateTimeParts(value: string): { dateStr: string; timeStr: string } {
    const d = new Date(value);
    return {
      dateStr: this.toYyyyMmDd(d),
      timeStr: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
    };
  }

  openShuttleDatePopup(
    trip: Trip,
    point: TripPoint,
    mode: "departure" | "arrival",
    event?: Event,
  ): void {
    event?.stopPropagation();
    if (mode === "departure" && !(point as any).tripPointLoadingClosedAt) {
      if (this.isShuttleDepartureBlocked(trip, point)) {
        return;
      }
    }
    this.shuttlePopupMode = mode;
    this.shuttleDatePopupFor = { trip, point };
    this.shuttleStep = "input";
    this.shuttleValidationError = "";

    this.shuttleDepartureAlreadySet = !!(point as any).tripPointLoadingClosedAt;
    this.shuttleArrivalAlreadySet = !!(point as any).tripPointArrivalAt;

    const departure = this.shuttleDepartureAlreadySet
      ? this.dateTimeParts((point as any).tripPointLoadingClosedAt)
      : this.nowDateTimeParts();
    this.shuttleDepartureDateStr = departure.dateStr;
    this.shuttleDepartureTimeStr = departure.timeStr;

    const arrival = this.shuttleArrivalAlreadySet
      ? this.dateTimeParts((point as any).tripPointArrivalAt)
      : this.nowDateTimeParts();
    this.shuttleArrivalDateStr = arrival.dateStr;
    this.shuttleArrivalTimeStr = arrival.timeStr;

    this.shuttleLockCode = (point as any).tripPointLockCode || "";
  }

  closeShuttleDatePopup(): void {
    this.shuttleDatePopupFor = null;
    this.shuttleStep = "input";
    this.shuttleValidationError = "";
  }

  private combineDateTimeIso(dateStr: string, timeStr: string): string {
    const [y, m, d] = dateStr.split("-").map(Number);
    const [hh, mm] = timeStr.split(":").map(Number);
    return new Date(y, m - 1, d, hh, mm, 0).toISOString();
  }

  goToShuttleConfirmStep(): void {
    this.shuttleValidationError = "";

    if (
      this.shuttleDepartureEditable &&
      this.shuttleDatePopupFor &&
      this.isShuttleDepartureBlocked(
        this.shuttleDatePopupFor.trip,
        this.shuttleDatePopupFor.point,
      )
    ) {
      this.shuttleValidationError = this.shuttleDepartureBlockedReason(
        this.shuttleDatePopupFor.trip,
        this.shuttleDatePopupFor.point,
      );
      return;
    }

    if (this.shuttleDepartureEditable) {
      if (!this.shuttleDepartureDateStr || !this.shuttleDepartureTimeStr) {
        this.shuttleValidationError = "Inserire data e ora di partenza";
        return;
      }
      if (
        this.isFutureDateTime(
          this.shuttleDepartureDateStr,
          this.shuttleDepartureTimeStr,
        )
      ) {
        this.shuttleValidationError =
          "La data di partenza non può essere successiva ad adesso";
        return;
      }
      const readyAt = this.getDriverReadyAt(this.shuttleDatePopupFor?.point);
      if (
        readyAt &&
        new Date(
          this.combineDateTimeIso(
            this.shuttleDepartureDateStr,
            this.shuttleDepartureTimeStr,
          ),
        ).getTime() < new Date(readyAt).getTime()
      ) {
        this.shuttleValidationError = `La data di partenza non può essere precedente all'arrivo autista (${this.formatDateTime(readyAt)})`;
        return;
      }
    }

    if (this.shuttleArrivalEditable) {
      if (!this.shuttleArrivalDateStr || !this.shuttleArrivalTimeStr) {
        this.shuttleValidationError = "Inserire data e ora di arrivo";
        return;
      }
      if (
        this.isFutureDateTime(
          this.shuttleArrivalDateStr,
          this.shuttleArrivalTimeStr,
        )
      ) {
        this.shuttleValidationError =
          "La data di arrivo non può essere successiva ad adesso";
        return;
      }
    }

    this.shuttleStep = "confirm";
  }

  backToShuttleInputStep(): void {
    this.shuttleStep = "input";
    this.shuttleValidationError = "";
  }

  confirmShuttleDatePopup(): void {
    if (!this.shuttleDatePopupFor || this.shuttleSaving) {
      return;
    }
    const { trip, point } = this.shuttleDatePopupFor;

    const payload: any = {};
    if (this.shuttleDepartureEditable) {
      payload.tripPointLoadingClosedAt = this.combineDateTimeIso(
        this.shuttleDepartureDateStr,
        this.shuttleDepartureTimeStr,
      );
    }
    if (this.shuttleArrivalEditable) {
      payload.tripPointArrivalAt = this.combineDateTimeIso(
        this.shuttleArrivalDateStr,
        this.shuttleArrivalTimeStr,
      );
    }

    this.shuttleSaving = true;

    this.http
      .post(
        `${environment.apiUrl}/trips/points/${point.tripPointId}/shuttle-dates`,
        payload,
      )
      .pipe(finalize(() => (this.shuttleSaving = false)))
      .subscribe({
        next: (result: any) => {
          if (result) {
            Object.assign(point, result);
          }
          if (this.shuttleDepartureEditable) {
            (point as any).tripPointLoadingClosedAt =
              result?.tripPointLoadingClosedAt ||
              payload.tripPointLoadingClosedAt;
            (point as any).tripPointLoadingClosedBy =
              result?.tripPointLoadingClosedBy;
            // Con la prima data di partenza vettore e targa rimorchio del viaggio non sono piu' modificabili
            (trip as any).tripHeaderEditable = false;
          }
          if (this.shuttleArrivalEditable) {
            (point as any).tripPointArrivalAt =
              result?.tripPointArrivalAt || payload.tripPointArrivalAt;
            (point as any).tripPointArrivalBy = result?.tripPointArrivalBy;
          }
          this.notificationService.success("Dati navetta salvati con successo");
          this.closeShuttleDatePopup();
          // La partenza (setLeft) cambia stato del viaggio, del punto e dei punti di arrivo: ricarico il viaggio
          this.reloadTrip(trip.tripId);
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.shuttleValidationError = "";
          this.notificationService.error(`Errore nel salvataggio: ${message}`);
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Popup "Arrivo autista": data/ora in cui l'autista e' arrivato ed e' pronto a caricare,
  // insieme ai dati di trazione (vettore trazionista, autista, sigillo).
  // Si puo' registrare in qualsiasi momento prima della partenza (anche prima della testata
  // viaggio); una volta registrato si riapre solo in consultazione.
  // ---------------------------------------------------------------------------
  driverReadyPopupFor: { trip: Trip; point: TripPoint } | null = null;
  driverReadyDateStr = "";
  driverReadyTimeStr = "";
  driverReadyAlreadySet = false;
  driverReadySaving = false;
  driverReadyStep: "input" | "confirm" = "input";
  driverReadyValidationError = "";

  driverReadyCarrierCode: string | null = null;
  driverReadyTractorPlateNo = "";
  driverReadyDriverName = "";
  driverReadyLockCode = "";
  lockCodeScanned = false;

  @ViewChild("lockCodeInput") lockCodeInput?: ElementRef<HTMLInputElement>;

  getDriverReadyAt(point: TripPoint | any): string | null {
    return point?.tripPointDriverReadyAt || null;
  }

  /** Arrivo autista registrato con vettore trazionista e autista: requisito per la partenza. */
  hasDriverReady(point: TripPoint | any): boolean {
    return (
      !!this.getDriverReadyAt(point) &&
      !!point?.tripPointCarrierCode &&
      !!String(point?.tripPointDriverName || "").trim()
    );
  }

  /** L'arrivo autista si puo' inserire su un punto di carico finche' non c'e' la partenza. */
  canSetDriverReady(point: TripPoint | any): boolean {
    return (
      this.isDeparturePoint(point) &&
      !point?.tripPointLoadingClosedAt &&
      !this.getDriverReadyAt(point)
    );
  }

  get driverReadyEditable(): boolean {
    return !!this.driverReadyPopupFor && !this.driverReadyAlreadySet;
  }

  get driverReadyConfirmMessage(): string {
    return (
      `Sei sicuro di voler dichiarare l'arrivo dell'autista ${this.driverReadyDriverName?.trim()} ` +
      `il ${this.formatItDate(this.driverReadyDateStr)} alle ${this.driverReadyTimeStr}` +
      (this.driverReadyLockCode
        ? ` con sigillo ${this.driverReadyLockCode}`
        : "") +
      `? L'operazione non potrà essere modificata in seguito.`
    );
  }

  openDriverReadyPopup(trip: Trip, point: TripPoint, event?: Event): void {
    event?.stopPropagation();
    const alreadySet = !!this.getDriverReadyAt(point);
    // Senza arrivo autista e con la partenza gia' registrata non c'e' nulla da inserire ne' da vedere
    if (!alreadySet && !this.canSetDriverReady(point)) {
      return;
    }

    this.driverReadyPopupFor = { trip, point };
    this.driverReadyAlreadySet = alreadySet;
    this.driverReadyStep = "input";
    this.driverReadyValidationError = "";

    // Default: adesso; si puo' solo spostare indietro
    const readyAt = alreadySet
      ? this.dateTimeParts(this.getDriverReadyAt(point) as string)
      : this.nowDateTimeParts();
    this.driverReadyDateStr = readyAt.dateStr;
    this.driverReadyTimeStr = readyAt.timeStr;

    // Vettore trazionista, targa motrice e autista sono dati del singolo punto di carico.
    // Default: se il punto ha gia' i dati di trazione (es. inseriti alla creazione del borderò) li propongo,
    // altrimenti vettore = vettore del viaggio; targa motrice/autista = quelli del punto di carico precedente.
    this.driverReadyCarrierCode = alreadySet
      ? (point as any).tripPointCarrierCode || null
      : (point as any).tripPointCarrierCode ||
        (trip as any).tripCarrierCode ||
        null;
    this.driverReadyTractorPlateNo =
      (point as any).tripPointTractorPlateNo || "";
    this.driverReadyDriverName = (point as any).tripPointDriverName || "";
    this.driverReadyLockCode = (point as any).tripPointLockCode || "";
    this.lockCodeScanned = false;

    if (alreadySet) {
      return;
    }

    const openedFor = point;
    this.http
      .get<any>(
        `${environment.apiUrl}/trips/points/${point.tripPointId}/shuttle-defaults`,
      )
      .subscribe({
        next: (defaults) => {
          if (this.driverReadyPopupFor?.point !== openedFor) {
            return;
          }
          if (!this.driverReadyTractorPlateNo) {
            this.driverReadyTractorPlateNo =
              defaults?.tripPointTractorPlateNo || "";
          }
          if (!this.driverReadyDriverName) {
            this.driverReadyDriverName = defaults?.tripPointDriverName || "";
          }
        },
        error: () => {},
      });
  }

  closeDriverReadyPopup(): void {
    this.driverReadyPopupFor = null;
    this.driverReadyStep = "input";
    this.driverReadyValidationError = "";
  }

  goToDriverReadyConfirmStep(): void {
    this.driverReadyValidationError = "";

    if (!this.driverReadyDateStr || !this.driverReadyTimeStr) {
      this.driverReadyValidationError = "Inserire data e ora di arrivo autista";
      return;
    }
    if (this.isFutureDateTime(this.driverReadyDateStr, this.driverReadyTimeStr)) {
      this.driverReadyValidationError =
        "La data di arrivo autista non può essere successiva ad adesso";
      return;
    }
    if (!this.driverReadyCarrierCode) {
      this.driverReadyValidationError = "Selezionare il vettore trazionista";
      return;
    }
    // if (!this.driverReadyTractorPlateNo?.trim()) {
    //   this.driverReadyValidationError = 'Inserire la targa motrice';
    //   return;
    // }
    if (!this.driverReadyDriverName?.trim()) {
      this.driverReadyValidationError = "Inserire l'autista";
      return;
    }

    this.driverReadyLockCode = normalizeScanCode(this.driverReadyLockCode);
    this.driverReadyStep = "confirm";
  }

  backToDriverReadyInputStep(): void {
    this.driverReadyStep = "input";
    this.driverReadyValidationError = "";
  }

  confirmDriverReadyPopup(): void {
    if (!this.driverReadyPopupFor || this.driverReadySaving) {
      return;
    }
    const { trip, point } = this.driverReadyPopupFor;

    const payload: any = {
      tripPointDriverReadyAt: this.combineDateTimeIso(
        this.driverReadyDateStr,
        this.driverReadyTimeStr,
      ),
      tripPointCarrierCode: this.driverReadyCarrierCode,
      tripPointTractorPlateNo: this.driverReadyTractorPlateNo?.trim() || null,
      tripPointDriverName: this.driverReadyDriverName?.trim(),
      tripPointLockCode: normalizeScanCode(this.driverReadyLockCode) || null,
    };

    this.driverReadySaving = true;

    this.http
      .post(
        `${environment.apiUrl}/trips/points/${point.tripPointId}/driver-ready`,
        payload,
      )
      .pipe(finalize(() => (this.driverReadySaving = false)))
      .subscribe({
        next: (result: any) => {
          // Non uso Object.assign(point, result): il DTO restituito dal mapper non contiene i campi
          // calcolati al caricamento del viaggio (tripPointIsLeftAvailable, nomi utente, ...) e li
          // azzererebbe, facendo sparire il bottone "Inserisci data di partenza".
          // Aggiorno solo i campi dell'arrivo autista.
          const p = point as any;
          p.tripPointDriverReadyAt =
            result?.tripPointDriverReadyAt || payload.tripPointDriverReadyAt;
          p.tripPointDriverReadyBy = result?.tripPointDriverReadyBy;
          p.tripPointCarrierCode =
            result?.tripPointCarrierCode || payload.tripPointCarrierCode;
          p.tripPointTractorPlateNo =
            result?.tripPointTractorPlateNo ?? payload.tripPointTractorPlateNo;
          p.tripPointDriverName =
            result?.tripPointDriverName || payload.tripPointDriverName;
          p.tripPointLockCode =
            result?.tripPointLockCode ?? payload.tripPointLockCode;
          this.notificationService.success(
            "Arrivo autista registrato con successo",
          );
          this.closeDriverReadyPopup();
          this.reloadTrip(trip.tripId);
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.driverReadyValidationError = "";
          this.notificationService.error(`Errore nel salvataggio: ${message}`);
        },
      });
  }

  searchCarrierFn(term: string, item: any): boolean {
    const t = (term || "").toLowerCase();
    return !!(
      item.carrierName?.toLowerCase().includes(t) ||
      item.carrierCode?.toLowerCase().includes(t)
    );
  }

  // ---------------------------------------------------------------------------
  // Testata viaggio: modifica vettore e targa rimorchio (solo fino alla prima partenza)
  // ---------------------------------------------------------------------------
  tripHeaderPopupFor: Trip | null = null;
  tripHeaderCarrierCode: string | null = null;
  tripHeaderPlateNo = "";
  tripHeaderSaving = false;
  tripHeaderError = "";

  isTripHeaderComplete(trip: Trip): boolean {
    return (
      !!(trip as any).tripCarrierCode &&
      !!String((trip as any).tripPlateNo || "").trim()
    );
  }

  /**
   * Il bottone "Inserisci data di partenza" resta disabilitato finche' vettore e targa rimorchio
   * del viaggio non sono compilati e finche' non e' stato registrato l'arrivo autista
   * (con vettore trazionista e autista).
   */
  isShuttleDepartureBlocked(trip: Trip, point: TripPoint | any): boolean {
    return (
      this.isDeparturePoint(point) &&
      !point?.tripPointLoadingClosedAt &&
      (!this.isTripHeaderComplete(trip) || !this.hasDriverReady(point))
    );
  }

  /** Motivo per cui la partenza non e' ancora inseribile (tooltip / messaggio di validazione). */
  shuttleDepartureBlockedReason(trip: Trip, point: TripPoint | any): string {
    if (!this.isShuttleDepartureBlocked(trip, point)) {
      return "";
    }
    const missing: string[] = [];
    if (!this.isTripHeaderComplete(trip)) {
      missing.push(
        "compilare vettore e targa rimorchio del viaggio (matita in intestazione)",
      );
    }
    if (!this.hasDriverReady(point)) {
      missing.push(
        "registrare l'arrivo autista con vettore trazionista e autista",
      );
    }
    const text = missing.join(" e ");
    return `Prima di inserire la partenza ${text}`;
  }

  canEditTripHeader(trip: Trip): boolean {
    return !!(trip as any).tripHeaderEditable;
  }

  openTripHeaderPopup(trip: Trip, event?: Event): void {
    event?.stopPropagation();
    if (!this.canEditTripHeader(trip)) {
      return;
    }
    this.tripHeaderPopupFor = trip;
    this.tripHeaderCarrierCode = (trip as any).tripCarrierCode || null;
    this.tripHeaderPlateNo = (trip as any).tripPlateNo || "";
    this.tripHeaderError = "";
  }

  closeTripHeaderPopup(): void {
    this.tripHeaderPopupFor = null;
    this.tripHeaderError = "";
  }

  saveTripHeader(): void {
    if (!this.tripHeaderPopupFor || this.tripHeaderSaving) {
      return;
    }
    if (!this.tripHeaderCarrierCode) {
      this.tripHeaderError = "Selezionare il vettore";
      return;
    }
    if (!this.tripHeaderPlateNo?.trim()) {
      this.tripHeaderError = "Inserire la targa rimorchio";
      return;
    }
    const trip = this.tripHeaderPopupFor;
    const payload = {
      tripCarrierCode: this.tripHeaderCarrierCode,
      tripPlateNo: this.tripHeaderPlateNo?.trim() || null,
    };

    this.tripHeaderSaving = true;
    this.http
      .post<any>(
        `${environment.apiUrl}/trips/${trip.tripId}/carrier-plate`,
        payload,
      )
      .pipe(finalize(() => (this.tripHeaderSaving = false)))
      .subscribe({
        next: (result) => {
          (trip as any).tripCarrierCode =
            result?.tripCarrierCode ?? payload.tripCarrierCode;
          (trip as any).tripPlateNo =
            result?.tripPlateNo ?? payload.tripPlateNo;
          this.notificationService.success(
            "Dati del viaggio salvati con successo",
          );
          this.closeTripHeaderPopup();
          this.reloadTrip(trip.tripId);
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.tripHeaderError = message || "Errore nel salvataggio";
        },
      });
  }

  changeDestinationPopupFor: { trip: Trip; point: TripPoint } | null = null;
  changeDestinationStep: 'input' | 'confirm' = 'input';
  changeDestinationWarehouseCode: string | null = null;
  changeDestinationCarrierCode: string | null = null;
  changeDestinationDriverName = '';
  changeDestinationWarehouses: Warehouse[] = [];
  changeDestinationSaving = false;
  changeDestinationError = '';

  canShowChangeDestination(trip: Trip, point: TripPoint | any): boolean {
    return this.isDeparturePoint(point)
      && !this.isExternalTrip(trip)
      && trip.tripStatus !== 'DELETED'
      && this.canManageShuttle(point)
      && this.getPointDestinationDocuments(trip, point).length > 0
      && !point?.tripPointDestinationWarehouseCode
      && !this.isChangeDestinationBlocked(point)
      && this.getChangeDestinationWarehouseCodes(trip, point).length > 0;
  }

  cancelChangeDestinationFor: { trip: Trip; point: TripPoint } | null = null;
  cancelChangeDestinationSaving = false;
  cancelChangeDestinationError = '';

  canShowCancelChangeDestination(trip: Trip, point: TripPoint | any): boolean {
    return this.isDeparturePoint(point)
      && !this.isExternalTrip(trip)
      && trip.tripStatus !== 'CLOSED'
      && trip.tripStatus !== 'DELETED'
      && this.canManageShuttle(point)
      && !!point?.tripPointDestinationWarehouseCode
      && !this.isChangeDestinationBlocked(point);
  }

  openCancelChangeDestinationPopup(trip: Trip, point: TripPoint, event?: Event): void {
    event?.stopPropagation();
    this.cancelChangeDestinationFor = { trip, point };
    this.cancelChangeDestinationError = '';
  }

  closeCancelChangeDestinationPopup(): void {
    this.cancelChangeDestinationFor = null;
    this.cancelChangeDestinationError = '';
  }

  confirmCancelChangeDestination(): void {
    if (!this.cancelChangeDestinationFor || this.cancelChangeDestinationSaving) {
      return;
    }
    const { trip, point } = this.cancelChangeDestinationFor;
    const restrictWarehouseCode = this.isSingleWarehouseUser() ? this.myWarehouseCode() : null;
    const params: any = restrictWarehouseCode ? { restrictWarehouseCode } : {};

    this.cancelChangeDestinationSaving = true;
    this.http.post<Trip>(`${environment.apiUrl}/trips/points/${point.tripPointId}/cancel-change-destination`, null, { params })
      .pipe(finalize(() => (this.cancelChangeDestinationSaving = false)))
      .subscribe({
        next: (result) => {
          if (result) {
            Object.assign(trip, result);
          }
          delete this.parcelsByTrip[trip.tripId];
          this.notificationService.success(`Cambio destinazione del viaggio ${trip.tripCode} annullato`);
          this.closeCancelChangeDestinationPopup();
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.cancelChangeDestinationError = message || "Errore nell'annullamento";
        }
      });
  }

  private getChangeDestinationWarehouseCodes(trip: Trip, point: TripPoint | any): string[] {
    const destinations = new Set(this.getPointDestinationDocuments(trip, point).map(doc => doc.transportDocumentPointToWarehouseCode || doc.transportDocumentPointToDealerCode));
    const excluded = new Set([point?.tripPointWarehouseCode, ...(destinations.size === 1 ? destinations : [])]);
    return (point?.tripPointTransitWarehouseCodes || []).filter((code: string) => !excluded.has(code));
  }

  isChangeDestinationBlocked(point: TripPoint | any): boolean {
    return point?.tripPointIsLeftAvailable === false;
  }

  private getPointDestinationDocuments(trip: Trip, point: TripPoint | any): TripTransportDocument[] {
    return (trip.transportDocuments || []).filter(doc => doc.transportDocumentPointFromId === point?.tripPointId);
  }

  getPointDestinationLabel(trip: Trip, point: TripPoint | any): string {
    const labels = this.getPointDestinationDocuments(trip, point)
      .map(doc => this.getPointShortLabel(doc.transportDocumentPointToWarehouseCode, (doc as any).transportDocumentPointToDealerCode));
    return Array.from(new Set(labels)).join(', ') || '-';
  }

  get changeDestinationWarehouseName(): string {
    const wh = this.warehouses.find(w => w.warehouseCode === this.changeDestinationWarehouseCode);
    return wh ? `${wh.warehouseName} (${wh.warehouseShortName})` : (this.changeDestinationWarehouseCode || '');
  }

  openChangeDestinationPopup(trip: Trip, point: TripPoint, event?: Event): void {
    event?.stopPropagation();
    const allowed = new Set(this.getChangeDestinationWarehouseCodes(trip, point));
    this.changeDestinationWarehouses = this.warehouses.filter(w => allowed.has(w.warehouseCode));
    this.changeDestinationPopupFor = { trip, point };
    this.changeDestinationStep = 'input';
    this.changeDestinationWarehouseCode = null;
    this.changeDestinationCarrierCode = null;
    this.changeDestinationDriverName = '';
    this.changeDestinationError = '';
  }

  closeChangeDestinationPopup(): void {
    this.changeDestinationPopupFor = null;
    this.changeDestinationStep = 'input';
    this.changeDestinationError = '';
  }

  goToChangeDestinationConfirmStep(): void {
    this.changeDestinationError = '';
    if (!this.changeDestinationWarehouseCode) {
      this.changeDestinationError = 'Selezionare il magazzino di destinazione';
      return;
    }
    if (!this.changeDestinationCarrierCode) {
      this.changeDestinationError = 'Selezionare il vettore trazionista';
      return;
    }
    if (!this.changeDestinationDriverName?.trim()) {
      this.changeDestinationError = 'Inserire l\'autista';
      return;
    }
    this.changeDestinationStep = 'confirm';
  }

  backToChangeDestinationInputStep(): void {
    this.changeDestinationStep = 'input';
    this.changeDestinationError = '';
  }

  confirmChangeDestination(): void {
    if (!this.changeDestinationPopupFor || this.changeDestinationSaving) {
      return;
    }
    const { trip, point } = this.changeDestinationPopupFor;
    const payload = {
      warehouseToCode: this.changeDestinationWarehouseCode,
      tripPointCarrierCode: this.changeDestinationCarrierCode,
      tripPointDriverName: this.changeDestinationDriverName?.trim(),
      restrictWarehouseCode: this.isSingleWarehouseUser() ? this.myWarehouseCode() : null
    };

    this.changeDestinationSaving = true;
    this.http.post<Trip>(`${environment.apiUrl}/trips/points/${point.tripPointId}/change-destination`, payload)
      .pipe(finalize(() => (this.changeDestinationSaving = false)))
      .subscribe({
        next: (result) => {
          if (result) {
            Object.assign(trip, result);
          }
          delete this.parcelsByTrip[trip.tripId];
          this.notificationService.success(`Destinazione del viaggio ${trip.tripCode} modificata`);
          this.closeChangeDestinationPopup();
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.changeDestinationError = message || 'Errore nel salvataggio';
        }
      });
  }

  // ---------------------------------------------------------------------------
  // Tab Percorso: dettaglio partenza da un punto di carico
  // ---------------------------------------------------------------------------
  routeDetailPopupFor: { trip: Trip; point: TripPoint } | null = null;

  isDeparturePoint(point: TripPoint | any): boolean {
    return (
      point?.tripPointOperation === "LOADING" ||
      point?.tripPointOperation === "LOADING_UNLOADING"
    );
  }

  isArrivalPoint(point: TripPoint | any): boolean {
    return (
      point?.tripPointOperation === "UNLOADING" ||
      point?.tripPointOperation === "LOADING_UNLOADING"
    );
  }

  openRouteDetailPopup(trip: Trip, point: TripPoint, event?: Event): void {
    event?.stopPropagation();
    if (!this.isDeparturePoint(point)) {
      return;
    }
    this.routeDetailPopupFor = { trip, point };
  }

  closeRouteDetailPopup(): void {
    this.routeDetailPopupFor = null;
  }

  openLoadingListDetail(ll: any, trip?: Trip): void {
    const isLineCheck =
      (ll?.loadingListKind || "").toString().toUpperCase() === "LINE_CHECK";

    const dialogRef = this.dialog.open(NewLoadingListDialogComponent, {
      width: "1050px",
      maxWidth: "95vw",
      panelClass: "app-popup-dialog",
      autoFocus: false,
      data: {
        mode: "EDIT",
        loadingList: ll,
        warehouses: this.warehouses,
        carriers: this.carriers,
        tourGroups: ll?.loadingListTourRoutingGroups,
        myWarehouseCode: this.appliedWarehouse,
        readOnly: isLineCheck,
        linkedTripCode: ll?.loadingListTripCode || trip?.tripCode || null,
        showAllItemDates:
          !this.isSingleWarehouseUser() ||
          (!!trip && this.isExternalTrip(trip)),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result || isLineCheck) return;

      this.loadingListService.updateBordero(result).subscribe({
        next: () => this.refreshTrips(),
        error: (err: any) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(`Errore: ${message}`);
        },
      });
    });
  }

  openCreateLoadingList(trip?: Trip): void {
    const dialogRef = this.dialog.open(NewLoadingListDialogComponent, {
      width: "1050px",
      maxWidth: "95vw",
      panelClass: "app-popup-dialog",
      autoFocus: false,
      data: {
        mode: "CREATE",
        warehouses: this.filterableWarehouses,
        carriers: this.carriers,
        linkedTripCode: trip?.tripCode ?? null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.isLoading = true;

      const { tractionInfo, ...createRequest } = result as any;
      const linkedTripCode: string | null =
        createRequest?.linkedTripCode ?? null;

      const warnings: string[] = [];

      this.loadingListService
        .createLoadingList(createRequest)
        .pipe(
          switchMap((created: any) => {
            const attach$: Observable<unknown> = linkedTripCode
              ? this.tripService
                  .attachLoadingListToTrip({
                    loadingListId: created.loadingListId,
                    scannedCode: linkedTripCode,
                    warehouseCode: createRequest.warehouseCode,
                  })
                  .pipe(
                    catchError((err: any) => {
                      warnings.push(
                        `Borderò creato ma non collegato al viaggio: ${this.errorHelperService.extractBackendMessage(err)}`,
                      );
                      return of(null);
                    }),
                  )
              : of(null);

            return attach$.pipe(
              switchMap(() =>
                tractionInfo
                  ? this.tripService
                      .setLoadingListTraction(
                        created.loadingListId,
                        tractionInfo,
                      )
                      .pipe(
                        catchError((err: any) => {
                          warnings.push(
                            `Borderò creato ma dati trazione non salvati: ${this.errorHelperService.extractBackendMessage(err)}`,
                          );
                          return of(null);
                        }),
                      )
                  : of(null),
              ),
              map(() => created),
            );
          }),
          finalize(() => (this.isLoading = false)),
        )
        .subscribe({
          next: () => {
            warnings.forEach((w) => this.notificationService.error(w));
            this.refreshTrips();
          },
          error: (err: any) => {
            const message = this.errorHelperService.extractBackendMessage(err);
            this.notificationService.error(`Errore: ${message}`);
          },
        });
    });
  }

  linkLoadingListsPopupOpen = false;
  linkLoadingListsPopupTrip: Trip | null = null;
  linkLoadingListsPopupStep: "select" | "confirm" = "select";
  linkLoadingListsLoading = false;
  availableLoadingListsToLink: LoadingLists[] = [];
  selectedLoadingListsToLink: Record<number, boolean> = {};
  linkLoadingListsFilterText = "";
  linkingLoadingLists = false;

  openLinkLoadingListsPopup(trip: Trip): void {
    this.linkLoadingListsPopupTrip = trip;
    this.linkLoadingListsPopupStep = "select";
    this.selectedLoadingListsToLink = {};
    this.linkLoadingListsFilterText = "";
    this.linkLoadingListsPopupOpen = true;
    this.loadAvailableLoadingListsToLink();
  }

  closeLinkLoadingListsPopup(): void {
    this.linkLoadingListsPopupOpen = false;
    this.linkLoadingListsPopupTrip = null;
    this.availableLoadingListsToLink = [];
    this.selectedLoadingListsToLink = {};
    this.linkLoadingListsFilterText = "";
  }

  private loadAvailableLoadingListsToLink(): void {
    const trip = this.linkLoadingListsPopupTrip;
    if (!trip) return;

    const warehouseCode = trip.tripWarehouseFromCode || this.appliedWarehouse;

    this.linkLoadingListsLoading = true;
    this.selectedLoadingListsToLink = {};

    forkJoin([
      this.loadingListService.searchLoadingLists(
        this.buildLinkableLoadingListsSearchPayload(
          warehouseCode,
          "DRAFT",
          this.clientsBrands,
        ),
      ),
      this.loadingListService.searchLoadingLists(
        this.buildLinkableLoadingListsSearchPayload(
          warehouseCode,
          "PREPARED",
          this.clientsBrands,
        ),
      ),
    ])
      .pipe(finalize(() => (this.linkLoadingListsLoading = false)))
      .subscribe({
        next: ([draftLists, preparedLists]) => {
          this.availableLoadingListsToLink = [
            ...(draftLists || []),
            ...(preparedLists || []),
          ]
            .filter((ll) => ll.loadingListTripId !== trip.tripId)
            .sort(
              (a, b) =>
                new Date(b.loadingListCreatedAt ?? 0).getTime() -
                new Date(a.loadingListCreatedAt ?? 0).getTime(),
            );
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(`Errore: ${message}`);
          this.availableLoadingListsToLink = [];
        },
      });
  }

  private buildLinkableLoadingListsSearchPayload(
    warehouseCode: string | null,
    status: string,
    clientsBrands: ClientsBrands[],
  ): any {
    return {
      warehouseCode: warehouseCode || null,
      status,
      dateStart: null,
      dateEnd: null,
      clients: clientsBrands,
      loadingListCode: null,
      warehouseFromCode: null,
      warehouseToCode: null,
      parcelCode: null,
    };
  }

  isLoadingListSelectedToLink(ll: LoadingLists): boolean {
    return !!this.selectedLoadingListsToLink[ll.loadingListId];
  }

  toggleLoadingListSelection(ll: LoadingLists): void {
    this.selectedLoadingListsToLink[ll.loadingListId] =
      !this.selectedLoadingListsToLink[ll.loadingListId];
  }

  selectAllLoadingListsToLink(): void {
    this.filteredLoadingListsToLink.forEach(
      (ll) => (this.selectedLoadingListsToLink[ll.loadingListId] = true),
    );
  }

  deselectAllLoadingListsToLink(): void {
    this.selectedLoadingListsToLink = {};
  }

  get selectedLoadingListsToLinkCount(): number {
    return Object.values(this.selectedLoadingListsToLink).filter(Boolean)
      .length;
  }

  get selectedLoadingListsToLinkAlreadyLinkedCount(): number {
    return this.availableLoadingListsToLink.filter(
      (ll) => this.isLoadingListSelectedToLink(ll) && !!ll.loadingListTripId,
    ).length;
  }

  get filteredLoadingListsToLink(): LoadingLists[] {
    const term = (this.linkLoadingListsFilterText || "").trim().toLowerCase();
    if (!term) return this.availableLoadingListsToLink;

    return this.availableLoadingListsToLink.filter((ll) => {
      const haystack = [
        this.formatDateTime(ll.loadingListCreatedAt),
        ll.loadingListCode,
        ll.loadingListTripCode,
        this.getWarehouseName(ll.loadingListWarehouseCode),
        ll.loadingListWarehousesToName,
      ]
        .filter((v) => v !== null && v !== undefined)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }

  goToConfirmLinkStep(): void {
    if (this.selectedLoadingListsToLinkCount === 0) return;
    this.linkLoadingListsPopupStep = "confirm";
  }

  backToSelectLinkStep(): void {
    this.linkLoadingListsPopupStep = "select";
  }

  private attachOrRelinkLoadingList(
    ll: LoadingLists,
    trip: Trip,
  ): Observable<any> {
    const attachCall = () =>
      this.tripService.attachLoadingListToTrip({
        loadingListId: ll.loadingListId,
        scannedCode: trip.tripCode,
        warehouseCode: trip.tripWarehouseFromCode || this.appliedWarehouse,
        tripId: ll.loadingListTripId,
        forceCarrier: true,
      });

    return attachCall();
  }

  confirmLinkSelectedLoadingLists(): void {
    const trip = this.linkLoadingListsPopupTrip;
    if (!trip) return;

    const selected = this.availableLoadingListsToLink.filter((ll) =>
      this.isLoadingListSelectedToLink(ll),
    );
    if (!selected.length) return;

    this.linkingLoadingLists = true;

    const calls = selected.map((ll) =>
      this.attachOrRelinkLoadingList(ll, trip),
    );

    forkJoin(calls)
      .pipe(finalize(() => (this.linkingLoadingLists = false)))
      .subscribe({
        next: () => {
          this.closeLinkLoadingListsPopup();
          this.notificationService.success("Collegamento effettuato.");
          this.refreshTrips();
        },
        error: (err) => {
          this.closeLinkLoadingListsPopup();
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(
            `Errore durante il collegamento: ${message}`,
          );
        },
      });
  }

  getWarehouseName(code: string | null | undefined): string {
    if (!code) return "";
    const w = this.warehouses.find((x) => x.warehouseCode === code);
    return w ? `${w.warehouseName}` : code;
  }

  getWarehouseShortName(code: string | null | undefined): string {
    if (!code) return "";
    const w: any = this.warehouses.find((x) => x.warehouseCode === code);
    if (!w) return code;
    return w.warehouseShortName || w.warehouseName || code;
  }

  getPointShortLabel(
    warehouseCode: string | null | undefined,
    dealerCode: string | null | undefined,
    pointCode?: string | null,
  ): string {
    if (warehouseCode) return this.getWarehouseShortName(warehouseCode);
    if (dealerCode) return this.getDealerName(dealerCode) || dealerCode;
    return pointCode || "-";
  }

  getPointTooltip(
    warehouseCode: string | null | undefined,
    dealerCode: string | null | undefined,
    pointCode?: string | null,
  ): string {
    if (warehouseCode) {
      const w: any = this.warehouses.find(
        (x) => x.warehouseCode === warehouseCode,
      );
      if (!w) return warehouseCode;
      if (w.warehouseShortName && w.warehouseName)
        return `${w.warehouseShortName} (${w.warehouseName})`;
      return w.warehouseShortName || w.warehouseName || warehouseCode;
    }
    if (dealerCode) return this.getDealerName(dealerCode) || dealerCode;
    return pointCode || "-";
  }

  getCarrierName(code: string | null | undefined): string {
    if (!code) return "";
    const c = this.carriers.find((x) => x.carrierCode === code);
    return c ? (c as any).carrierName || code : code;
  }

  getDocumentTypeLabel(type: string | null | undefined): string {
    switch (type) {
      case "TRANSFER":
        return "Di trasferimento";
      case "DELIVERY":
        return "Di consegna";
      default:
        return type || "N/D";
    }
  }

  isTripCollapsed(trip: Trip): boolean {
    return !!this.collapsedTrips[trip.tripId];
  }

  toggleTripCollapsed(trip: Trip): void {
    this.collapsedTrips[trip.tripId] = !this.collapsedTrips[trip.tripId];
  }

  areAllTripsCollapsed(): boolean {
    return (
      this.trips.length > 0 &&
      this.trips.every((t) => this.collapsedTrips[t.tripId])
    );
  }

  toggleAllTripsCollapsed(): void {
    const collapseAll = !this.areAllTripsCollapsed();
    this.trips.forEach((t) => (this.collapsedTrips[t.tripId] = collapseAll));
  }

  isTripSelected(trip: Trip): boolean {
    return !!this.selectedTrips[trip.tripId];
  }

  toggleTripSelection(trip: Trip): void {
    this.selectedTrips[trip.tripId] = !this.selectedTrips[trip.tripId];
  }

  areAllTripsSelected(): boolean {
    return (
      this.trips.length > 0 &&
      this.trips.every((t) => this.selectedTrips[t.tripId])
    );
  }

  toggleSelectAllTrips(): void {
    const selectAll = !this.areAllTripsSelected();
    this.trips.forEach((t) => (this.selectedTrips[t.tripId] = selectAll));
  }

  get selectedTripsCount(): number {
    return this.trips.filter((t) => this.selectedTrips[t.tripId]).length;
  }

  get selectableSelectedTrips(): Trip[] {
    return this.trips.filter(
      (t) => this.selectedTrips[t.tripId] && this.canSendTripDocs(t),
    );
  }

  openBulkSendDocsConfirm(): void {
    if (this.selectableSelectedTrips.length === 0) return;
    this.showBulkSendDocsConfirm = true;
  }

  closeBulkSendDocsConfirm(): void {
    this.showBulkSendDocsConfirm = false;
  }

  confirmBulkSendTripDocs(): void {
    const trips = this.selectableSelectedTrips;
    if (!trips.length) {
      this.showBulkSendDocsConfirm = false;
      return;
    }

    let remaining = trips.length;

    trips.forEach((trip) => {
      this.sendingDocsTrip[trip.tripId] = true;

      this.tripService
        .requestTripDocsSend(trip.tripId)
        .pipe(
          finalize(() => {
            this.sendingDocsTrip[trip.tripId] = false;
            remaining--;
            if (remaining === 0) {
              this.showBulkSendDocsConfirm = false;
            }
          }),
        )
        .subscribe({
          next: (result: any) => {
            (trip as any).tripDocsSendRequestedAt =
              result?.tripDocsSendRequestedAt || new Date().toISOString();
            (trip as any).tripHasDocsEvents = true;
            this.selectedTrips[trip.tripId] = false;
          },
          error: (err) => {
            const message = this.errorHelperService.extractBackendMessage(err);
            this.notificationService.error(
              `Errore nell'invio delle bolle del viaggio ${trip.tripCode}: ${message}`,
            );
          },
        });
    });
  }

  toggleTripReportCollapsed(): void {
    this.isTripReportCollapsed = !this.isTripReportCollapsed;
  }

  /** Riepilogo per stato su tutti i risultati della ricerca, non solo sulla pagina visualizzata. */
  private loadStatusTotals(): void {
    if (!this.appliedFilters) return;

    this.statusTotalsSub?.unsubscribe();
    this.statusTotalsSub = this.tripService
      .searchStatusTotals(this.appliedFilters)
      .subscribe({
        next: (data) => this.applyStatusTotals(data),
        error: (err) => {
          this.applyStatusTotals(null);
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(
            `Errore nel caricamento del riepilogo viaggi: ${message}`,
          );
        },
      });
  }

  private applyStatusTotals(data: TripStatusTotals | null): void {
    const totals = (status: string): TripStatusTotal => {
      const t = data?.statusTotals?.[status];
      return {
        tripsCount: t?.tripsCount || 0,
        parcelsCount: t?.parcelsCount || 0,
        loadedWeight: t?.loadedWeight || 0,
        loadedVolume: t?.loadedVolume || 0,
      };
    };

    const planned = totals("PLANNED");
    this.summaryTripPlanned = planned.tripsCount;
    this.totTripPlannedItems = planned.parcelsCount;
    this.totTripPlannedWeight = planned.loadedWeight;
    this.totTripPlannedVolume = planned.loadedVolume;

    const inProgress = totals("IN_PROGRESS");
    this.summaryTripInProgress = inProgress.tripsCount;
    this.totTripInProgressItems = inProgress.parcelsCount;
    this.totTripInProgressWeight = inProgress.loadedWeight;
    this.totTripInProgressVolume = inProgress.loadedVolume;

    const closed = totals("CLOSED");
    this.summaryTripClosed = closed.tripsCount;
    this.totTripClosedItems = closed.parcelsCount;
    this.totTripClosedWeight = closed.loadedWeight;
    this.totTripClosedVolume = closed.loadedVolume;

    const deleted = totals("DELETED");
    this.summaryTripDeleted = deleted.tripsCount;
    this.totTripDeletedItems = deleted.parcelsCount;
    this.totTripDeletedWeight = deleted.loadedWeight;
    this.totTripDeletedVolume = deleted.loadedVolume;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  private nowStamp(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}${mm}${dd}_${hh}${mi}`;
  }

  printDDTFromTransportDocuments(doc: TripTransportDocument): void {
    const ids: number[] = [doc.transportDocumentId];

    this.printingDoc[doc.transportDocumentId] = true;

    this.tripService
      .printDDTFromTransportDocuments(ids)
      .pipe(finalize(() => (this.printingDoc[doc.transportDocumentId] = false)))
      .subscribe({
        next: (blob: Blob) => {
          this.downloadBlob(blob, `DDT_${this.nowStamp()}.pdf`);
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(`Errore: ${message}`);
        },
      });
  }

  printTransportDocument(id: number): void {
    const ids: number[] = [id];

    this.printingDoc[id] = true;

    this.tripService
      .printTransportDocuments(ids)
      .pipe(finalize(() => (this.printingDoc[id] = false)))
      .subscribe({
        next: (blob: Blob) =>
          this.downloadBlob(blob, `transport_document_${this.nowStamp()}.pdf`),
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(`Errore: ${message}`);
        },
      });
  }

  isDocSelected(doc: TripTransportDocument): boolean {
    return !!this.selectedDocs[doc.transportDocumentId];
  }

  toggleDocSelection(doc: TripTransportDocument): void {
    this.selectedDocs[doc.transportDocumentId] =
      !this.selectedDocs[doc.transportDocumentId];
  }

  areAllDocsSelected(trip: Trip): boolean {
    const docs = this.getVisibleTransportDocuments(trip);
    return (
      docs.length > 0 &&
      docs.every((d) => this.selectedDocs[d.transportDocumentId])
    );
  }

  toggleAllDocsSelection(trip: Trip): void {
    const selectAll = !this.areAllDocsSelected(trip);
    this.getVisibleTransportDocuments(trip).forEach(
      (d) => (this.selectedDocs[d.transportDocumentId] = selectAll),
    );
  }

  getSelectedDocsForTrip(trip: Trip): TripTransportDocument[] {
    return this.getVisibleTransportDocuments(trip).filter(
      (d) => this.selectedDocs[d.transportDocumentId],
    );
  }

  printSelectedTransportDocuments(trip: Trip): void {
    const selected = this.getSelectedDocsForTrip(trip);
    if (!selected.length) return;

    const ids = selected.map((d) => d.transportDocumentId);

    this.printingBulkDocs[trip.tripId] = true;

    this.tripService
      .printTransportDocuments(ids)
      .pipe(finalize(() => (this.printingBulkDocs[trip.tripId] = false)))
      .subscribe({
        next: (blob: Blob) =>
          this.downloadBlob(blob, `transport_documents_${this.nowStamp()}.pdf`),
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(`Errore: ${message}`);
        },
      });
  }

  openTripPointParcelsPopup(trip: Trip, point: TripPoint): void {
    this.selectedTripPoint = point;
    this.selectedTripPointTrip = trip;
    this.tripPointParcels = [];
    this.tripPointParcelsSearch = "";
    this.tripPointParcelsOnlyUrgent = false;
    this.tripPointParcelsLoading = true;
    this.tripPointParcelsTab = "load";

    this.tripService
      .getParcelsByTripPoint(point.tripPointId)
      .pipe(finalize(() => (this.tripPointParcelsLoading = false)))
      .subscribe({
        next: (items: TripPointParcel[]) => {
          this.tripPointParcels = items || [];
          this.tripPointParcelsTab =
            this.tripPointParcelsToLoad.length > 0 ? "load" : "unload";
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(
            `Errore nel recupero dei colli del punto tappa: ${message}`,
          );
        },
      });
  }

  closeTripPointParcelsPopup(): void {
    this.selectedTripPoint = null;
    this.selectedTripPointTrip = null;
    this.tripPointParcels = [];
  }

  clearTripPointParcelsSearch(): void {
    this.tripPointParcelsSearch = "";
  }

  toggleTripPointParcelsUrgentFilter(): void {
    this.tripPointParcelsOnlyUrgent = !this.tripPointParcelsOnlyUrgent;
  }

  selectTripPointParcelsTab(tab: "load" | "unload"): void {
    this.tripPointParcelsTab = tab;
  }

  get tripPointParcelsToLoad(): TripPointParcel[] {
    const pointId = this.selectedTripPoint?.tripPointId;
    if (pointId == null) return [];
    return this.tripPointParcels.filter(
      (p: TripPointParcel) => p?.parcelComplementaryDepPointId === pointId,
    );
  }

  get tripPointParcelsToUnload(): TripPointParcel[] {
    const pointId = this.selectedTripPoint?.tripPointId;
    if (pointId == null) return [];
    return this.tripPointParcels.filter(
      (p: TripPointParcel) => p?.parcelComplementaryArrPointId === pointId,
    );
  }

  get filteredTripPointParcels(): TripPointParcel[] {
    const search = (this.tripPointParcelsSearch || "").trim().toLowerCase();
    const source =
      this.tripPointParcelsTab === "load"
        ? this.tripPointParcelsToLoad
        : this.tripPointParcelsToUnload;

    return source.filter((p: TripPointParcel) => {
      if (this.tripPointParcelsOnlyUrgent && !p?.parcelUrgent) return false;

      if (!search) return true;

      const code = (p?.parcelCode || "").toString().toLowerCase();
      const barcode = (p?.parcelBarcode || "").toString().toLowerCase();

      return code.includes(search) || barcode.includes(search);
    });
  }

  get tripPointParcelsTotalWeight(): number {
    return this.tripPointParcels.reduce(
      (sum: number, p: TripPointParcel) => sum + (p?.parcelWeight || 0),
      0,
    );
  }

  get tripPointParcelsTotalVolume(): number {
    return this.tripPointParcels.reduce(
      (sum: number, p: TripPointParcel) => sum + (p?.parcelVolume || 0),
      0,
    );
  }

  get tripPointParcelsLoadWeight(): number {
    return this.tripPointParcelsToLoad.reduce(
      (sum: number, p: TripPointParcel) => sum + (p?.parcelWeight || 0),
      0,
    );
  }

  get tripPointParcelsLoadVolume(): number {
    return this.tripPointParcelsToLoad.reduce(
      (sum: number, p: TripPointParcel) => sum + (p?.parcelVolume || 0),
      0,
    );
  }

  get tripPointParcelsUnloadWeight(): number {
    return this.tripPointParcelsToUnload.reduce(
      (sum: number, p: TripPointParcel) => sum + (p?.parcelWeight || 0),
      0,
    );
  }

  get tripPointParcelsUnloadVolume(): number {
    return this.tripPointParcelsToUnload.reduce(
      (sum: number, p: TripPointParcel) => sum + (p?.parcelVolume || 0),
      0,
    );
  }

  get tripPointParcelsHasBothTabs(): boolean {
    return (
      this.tripPointParcelsToLoad.length > 0 &&
      this.tripPointParcelsToUnload.length > 0
    );
  }

  get tripPointParcelsUrgentCount(): number {
    return this.tripPointParcels.filter((p: TripPointParcel) => p?.parcelUrgent)
      .length;
  }

  trackByParcelRow(index: number, item: TripPointParcel): number | string {
    return item?.parcelId ?? item?.parcelCode ?? index;
  }

  getTripPointParcelStatusMeta(p: TripPointParcel): {
    label: string;
    done: boolean;
  } {
    const point = this.selectedTripPoint;
    const pointId = point?.tripPointId;
    if (pointId == null) return { label: "IN ATTESA", done: false };

    const loadedHere =
      p?.parcelComplementaryDepPointId === pointId &&
      (p?.parcelComplementaryStatus == "IN_PROGRESS" ||
        p?.parcelComplementaryStatus == "EXECUTED");
    const unloadedHere =
      p?.parcelComplementaryArrPointId === pointId &&
      p?.parcelComplementaryStatus == "EXECUTED";

    if (loadedHere) return { label: "CARICATO", done: true };
    if (unloadedHere) return { label: "SCARICATO", done: true };

    return { label: "IN ATTESA", done: false };
  }

  canSendTripDocs(trip: Trip): boolean {
    return (
      !this.isExternalTrip(trip) &&
      !(trip as any).tripDocsSendRequestedAt &&
      !(trip as any).tripDocsSentAt &&
      this.areLoadingPointsCompleted(trip) &&
      trip.tripHasDocsToSend === true
    );
  }

  getTripType(trip: Trip): string {
    return this.isExternalTrip(trip) ? "DELIVERY" : "TRANSFER";
  }

  getTripTypeMeta(trip: Trip): StatusMeta {
    const meta = this.availableTripTypes.find(
      (t) => t.value === this.getTripType(trip),
    )!;
    return {
      ...meta,
      label: this.isExternalTrip(trip) ? "Consegna" : "Trasferimento",
    };
  }

  isExternalTrip(trip: Trip): boolean {
    return !!trip.tripExternalTripCode;
  }

  isTripDocsPending(trip: Trip): boolean {
    return (
      !!(trip as any).tripDocsSendRequestedAt && !(trip as any).tripDocsSentAt
    );
  }

  isTripDocsSent(trip: Trip): boolean {
    return !!(trip as any).tripDocsSentAt;
  }

  getTripTourGroupsLabel(trip: Trip): string {
    const names = trip.tripTourGroupNames || [];
    const label = names.slice(0, this.maxTripTourGroupsShown).join(", ");
    return names.length > this.maxTripTourGroupsShown ? `${label}, …` : label;
  }

  getTripTourGroupsTooltip(trip: Trip): string {
    return (trip.tripTourGroupNames || []).join(", ");
  }

  openSendDocsConfirm(trip: Trip): void {
    this.showSendDocsConfirmFor = trip;
  }

  closeSendDocsConfirm(): void {
    this.showSendDocsConfirmFor = null;
  }

  confirmSendTripDocs(): void {
    const trip = this.showSendDocsConfirmFor;
    if (!trip) return;

    this.sendingDocsTrip[trip.tripId] = true;

    this.tripService
      .requestTripDocsSend(trip.tripId)
      .pipe(finalize(() => (this.sendingDocsTrip[trip.tripId] = false)))
      .subscribe({
        next: (result: any) => {
          (trip as any).tripDocsSendRequestedAt =
            result?.tripDocsSendRequestedAt || new Date().toISOString();
          (trip as any).tripDocsSendRequestedBy =
            result?.tripDocsSendRequestedBy;
          (trip as any).tripDocsSendRequestedByName =
            result?.tripDocsSendRequestedByName;
          (trip as any).tripHasDocsEvents = true;
          this.notificationService.success(
            `Richiesta di invio bolle del viaggio ${trip.tripCode} registrata: in attesa di conferma`,
          );
          this.showSendDocsConfirmFor = null;
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(
            `Errore nella richiesta di invio delle bolle del viaggio: ${message}`,
          );
        },
      });
  }

  revertTripDocsSend(trip: Trip): void {
    if (!this.isTripDocsPending(trip)) {
      return;
    }
    this.revertingDocsTrip[trip.tripId] = true;

    this.http
      .post(
        `${environment.apiUrl}/trips/${trip.tripId}/revert-docs-send-request`,
        {},
      )
      .pipe(finalize(() => (this.revertingDocsTrip[trip.tripId] = false)))
      .subscribe({
        next: () => {
          (trip as any).tripDocsSendRequestedAt = null;
          (trip as any).tripDocsSendRequestedBy = null;
          (trip as any).tripIsDocsToSend = false;
          this.notificationService.success(
            `Richiesta di invio bolle del viaggio ${trip.tripCode} annullata`,
          );
        },
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(
            `Errore nell'annullamento della richiesta: ${message}`,
          );
        },
      });
  }

  toYyyyMmDd(date: Date): string {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, "0");
    const d = `${date.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("it-IT");
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  formatKg(value: number | null | undefined): string {
    if (value == null) return "0 kg";
    return `${value.toLocaleString("it-IT", { maximumFractionDigits: 2 })} kg`;
  }

  formatM3(value: number | null | undefined): string {
    if (value == null) return "0 m³";
    return `${value.toLocaleString("it-IT", { maximumFractionDigits: 3 })} m³`;
  }

  trackByTripId(index: number, trip: Trip): number {
    return trip?.tripId ?? index;
  }

  trackByPointId(index: number, point: TripPoint): number {
    return point?.tripPointId ?? index;
  }

  trackByDocId(index: number, doc: TripTransportDocument): number {
    return doc?.transportDocumentId ?? index;
  }

  trackByTransportItemId(
    index: number,
    item: TripTransportDocumentItem,
  ): number {
    return item?.transportDocumentItemId ?? index;
  }

  getDocumentPointLabel(
    warehouseCode: string | null | undefined,
    dealerCode: string | null | undefined,
    pointCode: string | null | undefined,
  ): string {
    if (!pointCode) return "-";

    if (warehouseCode) {
      const warehouseName =
        this.getWarehouseName(warehouseCode) || warehouseCode;
      return `${warehouseName}`;
    }

    if (dealerCode) {
      const dealerName = this.getDealerName(dealerCode) || dealerCode;
      return `${dealerName}`;
    }

    return pointCode;
  }

  private toStartOfDayIso(yyyyMmDd: string): string | null {
    if (!yyyyMmDd) return null;
    const d = this.fromYyyyMmDd(yyyyMmDd);
    if (!d) return null;
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }

  private toEndOfDayIso(yyyyMmDd: string): string | null {
    if (!yyyyMmDd) return null;
    const d = this.fromYyyyMmDd(yyyyMmDd);
    if (!d) return null;
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  }

  getDealerName(code: string | undefined) {
    return this.dealers.filter((d) => d.dealerCode == code).length > 0
      ? this.dealers.filter((d) => d.dealerCode == code)[0].dealerName
      : null;
  }

  printDDTByTransportDocument(id: number): void {
    const ids: number[] = [id];

    this.printingDDTByDoc[id] = true;

    this.tripService
      .printDDTByTransportDocument(ids)
      .pipe(finalize(() => (this.printingDDTByDoc[id] = false)))
      .subscribe({
        next: (blob: Blob) =>
          this.downloadBlob(blob, `DDT_doc_${this.nowStamp()}.pdf`),
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(`Errore: ${message}`);
        },
      });
  }

  getUserNameSendRequestedBy(trip: Trip): string {
    if (this.canSendDDT() && !this.isExternalTrip(trip)) {
      return "da " + trip.tripDocsSendRequestedByName;
    }
    return "";
  }

  areLoadingPointsCompleted(trip: Trip): boolean {
    const loadingPoints = (trip.tripPoints || []).filter((p) =>
      this.isDeparturePoint(p),
    );
    return (
      loadingPoints.length > 0 &&
      loadingPoints.every((p) => p.tripPointLoadingStatus === "CLOSED")
    );
  }

  getDocsEventMeta(type: string): {
    label: string;
    icon: string;
    cssClass: string;
  } {
    return (
      this.docsEventMeta[type] || {
        label: type || "Evento",
        icon: "fa-circle-info",
        cssClass: "ev-neutral",
      }
    );
  }

  hasTripDocsHistory(trip: Trip): boolean {
    return !!(trip as any).tripHasDocsEvents;
  }

  openDocsHistory(trip: Trip, event?: Event): void {
    event?.stopPropagation();
    this.docsHistoryPopupFor = trip;
    this.docsHistoryEvents = [];
    this.docsHistoryLoading = true;

    this.http
      .get<TripDocsEvent[]>(
        `${environment.apiUrl}/trips/${trip.tripId}/docs-events`,
      )
      .pipe(finalize(() => (this.docsHistoryLoading = false)))
      .subscribe({
        next: (events) => (this.docsHistoryEvents = events || []),
        error: (err) => {
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(
            `Errore nel caricamento dello storico invio bolle: ${message}`,
          );
          this.docsHistoryPopupFor = null;
        },
      });
  }

  closeDocsHistory(): void {
    this.docsHistoryPopupFor = null;
    this.docsHistoryEvents = [];
  }
}